import { isError } from '@belong/api/helpers';
import {
  isMobileDataProductsResponse,
  isMobileProductsResponse,
  loadMobileProducts,
  ProductAvailabilityFilter
} from '@belong/api/hooks/useMobileProducts';
import OffersApi from '@belong/offers/src/services/OffersApi';
import { ICatalogOffer, IMobileDataProductsResponse, IMobileProductsResponse, PRODUCT_FAMILY } from '@belong/types';

export type ProductResponseTypes = IMobileProductsResponse | IMobileDataProductsResponse;

// Generic T is narrowable, but defaults to all possible response types
export interface IProductAndOfferResponse<T = ProductResponseTypes> {
  products: T;
  offers: ICatalogOffer[];
}

const getProductCodes = (products: ProductResponseTypes): string[] => {
  if (isMobileProductsResponse(products)) {
    return products.mobilePlans.map(plan => plan.productCode);
  }

  if (isMobileDataProductsResponse(products)) {
    return products.mobileDataPlans.map(plan => plan.productCode);
  }

  throw new Error('Unknown product response type');
};

const loadProducts = async (
  productFamily: PRODUCT_FAMILY,
  filter?: ProductAvailabilityFilter,
  resolvedUrl?: string
): Promise<IMobileProductsResponse | IMobileDataProductsResponse> => {
  switch (productFamily) {
    case PRODUCT_FAMILY.MOBILE_PLANS:
    case PRODUCT_FAMILY.MOBILE_DATA_PLANS:
      return loadMobileProducts(productFamily, filter, resolvedUrl);
    default:
      throw new Error(`No loader found for product family: ${productFamily}`);
  }
};

const loadAtlOffers = async (productFamily: PRODUCT_FAMILY, productCodes: string[]): Promise<ICatalogOffer[]> => {
  const response = await OffersApi.getAtlOffers(productFamily, productCodes);

  if (isError(response.body)) {
    throw new Error(`Failed to load offers for product family: ${productFamily}`);
  }

  return response.body.offersToAdd;
};

export const loadProductAndOffers = async <T = ProductResponseTypes>(
  productFamily: PRODUCT_FAMILY,
  fetchOffers: boolean = false,
  filter?: ProductAvailabilityFilter,
  resolvedUrl?: string
): Promise<IProductAndOfferResponse<T>> => {
  const products = await loadProducts(productFamily, filter, resolvedUrl);
  let offers: ICatalogOffer[] = [];

  if (fetchOffers) {
    offers = await loadAtlOffers(productFamily, getProductCodes(products));
  }

  return { products: products as T, offers };
};
